/**
 * Hawker Session service
 */
import { Injectable, Injector } from '@angular/core';
import { ValueGetterParams } from 'ag-grid-community';
import { Location } from '@angular/common';

import { BaseService } from '../base';
import {
  chain,
  chunk,
  filter,
  find,
  flatten,
  flow,
  forEach,
  get,
  head,
  includes,
  isEmpty,
  isEqual,
  map,
  merge,
  omit,
  pick,
  set,
  size,
  some,
  sortBy,
  toLower,
  unset,
} from 'lodash';
import { detectMobile } from '@geniuspos/ui-shared/modules/xdm-shared-components/components/utils';
import { I18n } from '@app/i18n/strings-keys';
import { BehaviorSubject, Observable, combineLatest, of } from 'rxjs';
import { map as rxMap, finalize, switchMap } from 'rxjs/operators';
import { EVENT_STATUS_VALUE } from '@shared/types/enums/event-status.enum';
import { SiteType } from '@app/shared/types/enums/site-type.enum';
import { XprtDataService } from '@shared/services/xprt-data/xprt-data.service';
import { HawkerCommissaryAssignmentService } from '@shared/services/hawker-commissary-assignment';
import { XUXListContainerComponent } from '@containers/base';
import { ENTITY_TYPES } from '../../types/enums/entityTypes.enum';
import { IEntityConfig } from '../../types/entities/entity-config.type';
import { GRID_ROW_SELECTION } from '@geniuspos/ui-shared/modules/xdm-shared-components/definitions/types/grid-action.type';
import {
  IModalDialogOptions,
  IModalDialogResult,
} from '../../types/ui/modal-options.type';
import {
  BTN_CANCEL,
  BTN_DONE,
} from '../../../text-content/button-templates/button-templates-content';
import { WIDGET_FAMILY } from '../../../../config/global-constants';
import { WIDGET_COMPONENT } from '../../types/enums/widget-component.enum';
import { IXVIListColumn } from '../../types/ui/xux-grid/xux-grid.type';
import {
  IBaseEntityType,
  IDocumentOperationOptions,
} from '../../types/entities/base/base.type';
import { IEvent } from '../../types/entities/xprt-entities/events/events.type';
import { lazyInject } from '../../../containers/base/decorators/lazy-inject.decorator';
import { UiActions } from '@app/store/actions/ui/ui.actions';
import { CELL_FILTER } from '@app/shared/types/enums/cell-filters.enum';
import { CELL_RENDERER } from '@app/shared/types/enums/cell-renderer.enum';
import {
  COMMON_ACTION_BUTTONS,
  DID_STATUS_ACTION_MAPPING,
  EntityId,
  IApiListResponse,
  IBulkUpdateDocumentsResult,
  IFilterQuery,
  ISiteTag,
  NotificationType,
} from '@app/shared/types';
import {
  TOASTER_ERROR_MESSAGE,
  TOASTER_SUCCESS_MESSAGE,
} from '@app/shared/constants/editor-messages';
import {
  COLUMN_SELECTOR_ID,
  DEFAULT_PAGE_SIZE,
  MAX_PAGE_SIZE,
} from '@config/index';
import { Metadata } from '@geniuspos/xenial-data-contracts/types/dm-entities/metadata/metadata';
import { FilterOptionType } from '@geniuspos/ui-shared/modules/xdm-shared-components/definitions/types/filter.type';
import {
  IHawkerSession,
  IHawkerSessionEmployee,
  IProductWithLoad,
  ISaleDepletionOrdersSummaryResponse,
  ISaleDepletionRawResponse,
} from '@app/shared/types/entities/hawker-session/hawker-session.types';
import {
  HAWKER_SESSION_STATUS_LABEL_TEXT_MAPPING,
  HAWKER_SESSION_STATUS_VALUE,
} from '@app/shared/types/entities/hawker-session/hawker-session-status.enum';
import { HAWKER_SESSION_FORM_FIELD_PATH } from './hawker-session.constant';
import { hawkerServiceConfig } from './hawker-session-service.config';
import { IActionEmitterParams } from '@app/components/xvi-grid/custom-components/cell-renderer-components/common-actions-renderer/common-actions-renderer.types';
import {
  batchRequestMap,
  defaultBatchMapAccumulatedPredict,
} from '@app/shared/rx-operators/batch-map';
import { rx } from '@app/shared/rx/rx-wrapper';
import { HawkerSessionExtraDataService } from './hawker-session-extra-data.service';

@Injectable()
export class HawkerSessionService extends BaseService {
  public static config: IEntityConfig = hawkerServiceConfig;
  public static ENTITY_TYPE: string = hawkerServiceConfig.ENTITY_TYPE;

  @lazyInject(XprtDataService) public xprtDataService: XprtDataService;
  @lazyInject(UiActions) public uiActions: UiActions;
  @lazyInject(Location) public location: Location;
  @lazyInject(HawkerSessionExtraDataService)
  public hawkerSessionExtraDataService: HawkerSessionExtraDataService;
  @lazyInject(HawkerCommissaryAssignmentService)
  private hawkerCommissaryAssignmentService: HawkerCommissaryAssignmentService;

  public quickFilterFields: string[] = ['session_id', 'hawker_name'];
  public xuxListContainer: XUXListContainerComponent;
  public selectedSiteId: string = '';
  public selectedEventId: string = '';
  public commissarySites: IBaseEntityType[] = [];

  private readonly selectedSiteId$: BehaviorSubject<string> =
    new BehaviorSubject<string>('');

  private readonly selectedEventId$: BehaviorSubject<string> =
    new BehaviorSubject<string>('');

  public readonly createButtonDisabled$: Observable<boolean> = combineLatest([
    this.selectedSiteId$,
    this.selectedEventId$,
  ]).pipe(rxMap(([siteId, eventId]) => isEmpty(siteId) || isEmpty(eventId)));

  public beforeStartListMethods: Function[] = [
    this.hookXuxListContainer,
    this.initAndPromptSelection,
  ];

  public beforeDestroyListMethods: Function[] = [this.cleanupSubscriptions];

  public hookXuxListContainer(
    xuxListContainerComponent: XUXListContainerComponent,
  ): void {
    this.xuxListContainer = xuxListContainerComponent;
  }

  public initAndPromptSelection(): void {
    if (isEmpty(this.selectedSiteId) && isEmpty(this.selectedEventId)) {
      this.promptSiteAndEventSelection();
    }
  }

  public async promptSiteAndEventSelection(
    forceShowModal: boolean = false,
  ): Promise<void> {
    try {
      this.commissarySites = await this.fetchAssignedCommissarySites();
    } catch {
      return;
    }

    if (isEmpty(this.commissarySites)) {
      await this.uiActions.confirm({
        text: this.i18nService.translate(
          I18n.Forms.HawkerSessionForm.NoAvailableCommissarySite,
        ),
        buttons: [
          {
            ...BTN_DONE,
            caption: I18n.Common.Close,
          },
        ],
      });

      this.location.back();

      return;
    }

    const selectedSite = await this.resolveSelectedSite(forceShowModal);

    if (!selectedSite) {
      return;
    }

    this.selectedSiteId = get(selectedSite, 'id');
    this.selectedSiteId$.next(this.selectedSiteId);
    const selectedEvent = get(selectedSite, 'event') as IEvent;
    this.selectedEventId = selectedEvent.id;
    this.selectedEventId$.next(this.selectedEventId);
    this.applyEventSelection(selectedEvent);
  }

  private async fetchAssignedCommissarySites(): Promise<IBaseEntityType[]> {
    const currentUserId = this.stateService.getCurrentUserId();

    const assignmentRes = await this.hawkerCommissaryAssignmentService
      .fetchList({
        filter: `commissary_assignments/user_id eq in(${currentUserId})`,
        select: ['event_id', 'commissary_assignments'],
      })
      .toPromise();

    const assignments: IBaseEntityType[] = get(assignmentRes, 'items', []);

    if (isEmpty(assignments)) {
      return [];
    }

    const assignedEventIds: string[] = map(assignments, 'event_id');
    const assignedSiteIdsByEvent: Record<string, string[]> = {};
    forEach(assignments, (assignment) => {
      const eventId = get(assignment, 'event_id', '');
      assignedSiteIdsByEvent[eventId] = map(
        filter(get(assignment, 'commissary_assignments', []), {
          user_id: currentUserId,
        }),
        'site_id',
      );
    });

    const [eventsData, userAssignedSites] = await Promise.all([
      this.xprtDataService
        .getEventsByFilter({
          filter: `_id eq in(${assignedEventIds.join(',')}) and status eq in(${EVENT_STATUS_VALUE.Open})`,
          select: ['name', 'status', 'id', 'event_days', 'available_sites'],
          include_available_sites: true,
          available_sites_select: 'id,business_type',
          filter_by_assigned_sites: true,
        })
        .toPromise(),
      this.xprtDataService
        .getAllSitesByFilter(
          {
            filter_by_assigned_sites: true,
            select: ['id', 'name', 'store_number', 'business_type', 'metadata'],
          },
          { throwError: true },
        )
        .toPromise(),
    ]);

    return this.buildCommissarySitesWithEvent(
      assignedSiteIdsByEvent,
      eventsData,
      userAssignedSites,
    );
  }

  public buildCommissarySitesWithEvent(
    assignedSiteIdsByEvent: Record<string, string[]>,
    eventsData: IEvent[],
    userAssignedSites: IBaseEntityType[],
  ): IBaseEntityType[] {
    const openEventMap = new Map(
      eventsData.map((event) => [get(event, 'id', ''), event]),
    );

    const validCommissarySiteMap = new Map(
      filter(
        userAssignedSites,
        (site) => get(site, 'business_type') === SiteType.VendingCommissary,
      ).map((site) => [get(site, 'id', ''), site]),
    );

    const sites: IBaseEntityType[] = [];

    forEach(Object.entries(assignedSiteIdsByEvent), ([eventId, siteIds]) => {
      const event = openEventMap.get(eventId);

      if (!event) {
        return;
      }

      const eventAvailableSiteIds = new Set(
        map(get(event, 'available_sites', []) as IBaseEntityType[], (s) =>
          get(s, 'id', ''),
        ),
      );

      forEach(siteIds, (siteId) => {
        if (!eventAvailableSiteIds.has(siteId)) {
          return;
        }

        const siteData = validCommissarySiteMap.get(siteId);

        if (!siteData) {
          return;
        }

        sites.push({ ...siteData, event });
      });
    });

    return sortBy(sites, (site) => toLower(get(site, 'store_number', '')));
  }

  public async resolveSelectedSite(
    forceShowModal: boolean = false,
  ): Promise<IBaseEntityType | null> {
    if (!forceShowModal && size(this.commissarySites) === 1) {
      return head(this.commissarySites);
    }

    const siteResult = await this.openSiteSelectorModal();

    if (!siteResult.confirmed) {
      if (isEmpty(this.selectedEventId)) {
        this.location.back();
      }

      return null;
    }

    const selectedSiteId = head(siteResult.value.siteId) as string;

    return find(this.commissarySites, { id: selectedSiteId }) || null;
  }

  public applyEventSelection(selectedEvent: IEvent): void {
    const eventName = get(selectedEvent, 'name', '');
    const eventDate = get(selectedEvent, ['event_days', 0, 'start_date_time']);
    const formattedDate = isEmpty(eventDate)
      ? ''
      : this.companyService.convertUTCDateWithTimeZone(eventDate, true);

    this.config.list.titleObs.next(`${eventName} - ${formattedDate}`);
    this.config.list.documentQuery = {
      ...this.config.list.documentQuery,
      filter: `event/id eq in(${this.selectedEventId}) and site_id eq in(${this.selectedSiteId})`,
    };
    this.xuxListContainer.updateListViewData({
      currentPage: 1,
    });
  }

  public async openSiteSelectorModal(): Promise<IModalDialogResult> {
    const subTitle$: BehaviorSubject<string> = new BehaviorSubject<string>('');
    const sitesDisplayCount$: BehaviorSubject<number> =
      new BehaviorSubject<number>(0);

    sitesDisplayCount$.subscribe((count: number): void => {
      const sitesSubTitle = this.i18nService.translate(
        I18n.Forms.GridMessages.ShowingEntities,
        {
          count,
          entities: this.i18nService.translate(
            I18n.EntityNames.siteSingularAndPlural,
          ),
        },
      );
      subTitle$.next(sitesSubTitle);
    });

    const entityIdsForMetadatas: EntityId[] = chain(this.commissarySites)
      .flatMap('metadata')
      .map('entity_id')
      .value();

    const modalOptions: IModalDialogOptions = merge(
      {},
      {
        title: I18n.Common.SiteSelector,
        validation: true,
        modalClass: 'modal-lg hidden-header-checkbox',
        subTitleObs: subTitle$,
        buttons: [
          {
            ...BTN_DONE,
            disabled: (value: any): boolean => isEmpty(value.siteId),
            caption: I18n.Forms.HawkerSessionForm.SelectSite,
          },
          BTN_CANCEL,
        ],
        value: {
          siteId: isEmpty(this.selectedSiteId) ? [] : [this.selectedSiteId],
        },
        schema: {
          type: 'object',
          properties: {
            siteId: {
              type: 'array',
            },
          },
        },
        formRules: {
          fields: {
            siteId: {
              required: true,
              title: null,
              ignoreAutoTitleWhenTranslate: true,
              widgetFamily: WIDGET_FAMILY.XUX,
              widget: WIDGET_COMPONENT.grid_list,
              widgetData: {
                displayCountObs: sitesDisplayCount$,
                userGridOptions: {
                  rowSelection: GRID_ROW_SELECTION.SINGLE,
                  overlayNoRowsTemplate: I18n.Common.NoRowsToShow,
                },
                valueField: 'id',
                gridHeight: '400px',
                selectOptions: this.commissarySites,
                columns: [
                  {
                    title: I18n.Sites.SiteId,
                    path: 'store_number',
                    defaultView: true,
                    sortable: true,
                  },
                  {
                    title: I18n.Sites.SiteName,
                    path: 'name',
                    defaultView: true,
                    sortable: true,
                    filterParams: {
                      quickFilter: true,
                    },
                  },
                  {
                    title: I18n.EntityNames.Tags,
                    path: 'metadata',
                    defaultView: true,
                    sortable: true,
                    filterType: CELL_FILTER.entity,
                    filterParams: {
                      quickFilter: true,
                      entity: ENTITY_TYPES.TAG,
                      entitySelector: (item: ISiteTag): boolean =>
                        includes(entityIdsForMetadatas, item.entity_id),
                      default: {
                        type: 'containsAny' as FilterOptionType,
                      },
                      filterOptions: ['containsAny'] as FilterOptionType[],
                      filterItemSourceQuery: {
                        filter: `metadata_type eq 'tag'`,
                        select: ['entity_id', 'name', '_id'],
                      },
                      filterValueGetter: (data): EntityId[] =>
                        flatten(map(data.metadata, 'entity_id')),
                    },
                    valueGetter: (params: ValueGetterParams): string => {
                      const metadata: Metadata[] = get(
                        params,
                        'data.metadata',
                        [],
                      );

                      return chain(metadata)
                        .filter({ metadata_type: 'tag' })
                        .map('name')
                        .join(', ')
                        .value();
                    },
                  },
                ],
              },
            },
          },
        },
      },
    );

    const dialogResult = await this.uiActions.promptFormModal(modalOptions);

    return {
      confirmed: dialogResult.confirmed,
      modalResult: dialogResult.modalResult,
      value: dialogResult.value,
    };
  }

  public selectSiteAndEventHandler = async (): Promise<void> => {
    await this.promptSiteAndEventSelection(true);
  };

  public navigateToAddWithParams = (): void => {
    this.apiActions.openDocumentEditor(
      this.config.ENTITY_TYPE,
      'add',
      undefined,
      {
        site_id: this.selectedSiteId,
        event_id: this.selectedEventId,
      },
    );
  };

  public cleanupSubscriptions(): void {
    this.selectedSiteId$.complete();
    this.selectedEventId$.complete();
  }

  protected setupDocumentOperationOptions(): void {
    this.config.list.documentOperationOptions = {
      continueFetchCondition: (): boolean => {
        return !isEmpty(this.selectedSiteId) && !isEmpty(this.selectedEventId);
      },
    };
  }

  protected setupButtons(): void {
    this.config.buttons = {
      select_site: {
        click: this.selectSiteAndEventHandler,
      },
      create: {
        click: this.navigateToAddWithParams,
        disabledObs: this.createButtonDisabled$,
      },
      edit: false,
      edit_session: {
        value: 'edit_session',
        caption: I18n.Common.Edit,
        hidden: (selectedItems): boolean =>
          isEmpty(selectedItems) ||
          size(selectedItems) !== 1 ||
          !this.canManage ||
          (head(selectedItems) as IBaseEntityType)?.status ===
            HAWKER_SESSION_STATUS_VALUE.Canceled,
        click: (selectedItems: IBaseEntityType[]): void => {
          const documentId = get(head(selectedItems), '_id', '');
          if (documentId) {
            this.apiActions.openDocumentEditor(
              this.config.ENTITY_TYPE,
              documentId,
            );
          }
        },
      },
      view_session: {
        value: 'view_session',
        caption: I18n.Common.View,
        hidden: (selectedItems): boolean =>
          isEmpty(selectedItems) ||
          size(selectedItems) !== 1 ||
          (this.canManage &&
            (head(selectedItems) as IBaseEntityType)?.status !==
              HAWKER_SESSION_STATUS_VALUE.Canceled),
        click: (selectedItems: IBaseEntityType[]): void => {
          const documentId = get(head(selectedItems), '_id', '');
          if (documentId) {
            this.apiActions.openDocumentEditor(
              this.config.ENTITY_TYPE,
              documentId,
            );
          }
        },
      },
      cancel_session: {
        value: 'cancel_session',
        caption: I18n.Forms.HawkerSessionForm.CancelSession,
        hidden: (selectedItems): boolean =>
          !this.canManage ||
          size(selectedItems) < 1 ||
          some(
            selectedItems,
            (item) => item?.status !== HAWKER_SESSION_STATUS_VALUE.Open,
          ),
        click: this.cancelSession.bind(this),
      },
    };
  }

  protected setupListColumns(): void {
    set(this.config, 'list.columns', <IXVIListColumn[]>[
      {
        path: 'internal_number',
        title: I18n.Forms.HawkerSessionForm.SessionId,
        defaultView: true,
        sortable: true,
        filterType: CELL_FILTER.string,
        filterParams: {
          quickFilter: true,
        },
        cellRenderer: CELL_RENDERER.link_renderer,
        cellRendererParams: {
          entityType: this.config.ENTITY_TYPE,
        },
      },
      {
        path: 'hawker_name',
        title: I18n.EntityNames.Hawker,
        defaultView: true,
        filterType: CELL_FILTER.string,
        sortable: true,
        filterParams: {
          quickFilter: true,
        },
      },
      {
        path: 'employee.badge_number',
        title: I18n.Forms.GridForm.BadgeNumber,
        filterType: CELL_FILTER.string,
        defaultView: true,
      },
      {
        path: 'status',
        title: I18n.Common.Status,
        defaultView: true,
        sortable: true,
        filterType: CELL_FILTER.string,
        cellRenderer: CELL_RENDERER.prepared_string_renderer,
        cellRendererParams: {
          valueToEnumMap: HAWKER_SESSION_STATUS_LABEL_TEXT_MAPPING,
        },
      },
      {
        path: 'site_name',
        title: I18n.EntityNames.Site,
        defaultView: true,
        filterType: CELL_FILTER.string,
      },
      {
        path: 'commission_rate.name',
        title: I18n.Forms.HawkerProfileForm.Rate,
        defaultView: true,
        sortable: true,
      },
      {
        path: COLUMN_SELECTOR_ID,
        width: 50,
        maxWidth: 50,
        title: '',
        defaultView: true,
        cellRenderer: CELL_RENDERER.common_actions_renderer,
        cellRendererParams: {
          entityType: this.config.ENTITY_TYPE,
          actionButtons: [
            {
              ...COMMON_ACTION_BUTTONS.Edit,
              visible: (item: IBaseEntityType): boolean =>
                item?.status !== HAWKER_SESSION_STATUS_VALUE.Canceled &&
                this.canManage,
            },
            {
              ...COMMON_ACTION_BUTTONS.View,
              visible: (item: IBaseEntityType): boolean =>
                !this.canManage ||
                item?.status === HAWKER_SESSION_STATUS_VALUE.Canceled,
            },
            {
              ...COMMON_ACTION_BUTTONS.Cancel,
              name: I18n.Forms.HawkerSessionForm.CancelSession,
              visible: (item: IBaseEntityType): boolean =>
                item?.status === HAWKER_SESSION_STATUS_VALUE.Open &&
                this.canManage,
              click: (emitParams: IActionEmitterParams): Promise<void> => {
                return this.cancelSession([emitParams.data]);
              },
            },
          ],
        },
        pinned: detectMobile() ? false : 'right',
        lockPinned: true,
      },
    ]);
  }

  public constructor(public injector: Injector) {
    super(injector);
    this.setupDocumentOperationOptions();
    this.setupButtons();
    this.setupListColumns();
  }

  public fetchListByEmployeeIds(
    employeeIds: string[],
    userQuery?: IFilterQuery,
    options: IDocumentOperationOptions & {
      bulkSize?: number;
      withSiteInfo?: boolean;
      withEventInfo?: boolean;
    } = {},
  ): Observable<IApiListResponse> {
    const { bulkSize } = options ?? {};

    if (isEmpty(employeeIds)) {
      return this.fetchList(userQuery, options);
    }

    return batchRequestMap(
      chunk(employeeIds, bulkSize ?? DEFAULT_PAGE_SIZE),
      (chunkEmployeeIds) => {
        const curEmployeeFilter = `employee/id eq in(${chunkEmployeeIds.join(',')})`;
        const combinedFilter = !isEmpty(userQuery?.filter)
          ? `(${curEmployeeFilter}) and (${userQuery.filter})`
          : curEmployeeFilter;

        return this.fetchList(
          {
            ...userQuery,
            filter: combinedFilter,
            top: MAX_PAGE_SIZE,
          },
          {
            ...omit(options, ['withSiteInfo', 'withEventInfo']),
            fetchFullListData: true,
          },
        );
      },
    ).pipe(
      rx.reduce(defaultBatchMapAccumulatedPredict, {} as IApiListResponse),
      rx.switchMap((hawkerSessionsResponse) => {
        return this.hawkerSessionExtraDataService.fetchSiteAndEventInfoForSessions(
          hawkerSessionsResponse,
          options,
        );
      }),
      this.catchHttpError(options.errorOverride),
    );
  }

  public async cancelSession(selectedItems: IBaseEntityType[]): Promise<void> {
    const openSessions = filter(selectedItems, (item) =>
      isEqual(item?.status, HAWKER_SESSION_STATUS_VALUE.Open),
    ) as IHawkerSession[];

    if (isEmpty(openSessions)) {
      return;
    }

    const isSingle = openSessions.length === 1;
    const confirmText = isSingle
      ? this.i18nService.translate(
          I18n.ConfirmMessages.AreYouWantToBulkUpdateTarget,
          {
            wantTo: this.i18nService.translate(I18n.Common.cancel),
            target: this.i18nService.translate(
              I18n.Forms.HawkerSessionForm.Session,
            ),
          },
        )
      : this.i18nService.translate(
          I18n.ConfirmMessages.AreYouWantToBulkUpdateHowManyTarget,
          {
            wantTo: this.i18nService.translate(I18n.Common.cancel),
            count: openSessions.length,
            target: this.i18nService.translate(
              I18n.Forms.HawkerSessionForm.SessionsBracketsLowerCase,
            ),
          },
        );

    const result = await this.uiActions.confirm({
      text: confirmText,
    });

    if (!result.confirmed) {
      return;
    }

    await this.performCancelSessions(openSessions).toPromise();

    this.xuxListContainer?.refresh();
  }

  public performCancelSessions(
    sessions: IBaseEntityType[],
  ): Observable<IBulkUpdateDocumentsResult> {
    this.uiActions.enableLoader();

    const totalCount = size(sessions);
    const isSingle = totalCount === 1;

    return this.bulkUpdateEntity(
      map(sessions, (session) => ({
        _id: session._id,
        status: HAWKER_SESSION_STATUS_VALUE.Canceled,
      })),
    ).pipe(
      switchMap((result: IBulkUpdateDocumentsResult) => {
        const successCount = get(result, 'successCount', 0);
        const failedCount = totalCount - successCount;

        const canceledMapping =
          DID_STATUS_ACTION_MAPPING[HAWKER_SESSION_STATUS_VALUE.Canceled];

        if (isSingle) {
          if (failedCount <= 0) {
            this.uiActions.showToasterMessage({
              type: NotificationType.SUCCESS,
              title: TOASTER_SUCCESS_MESSAGE.title,
              body: this.i18nService.instant(
                I18n.InfoMessages.Didsuccessfully,
                {
                  did: this.i18nService.instant(canceledMapping.upperCase),
                },
              ),
            });
          } else {
            this.uiActions.showToasterMessage({
              type: NotificationType.ERROR,
              title: TOASTER_ERROR_MESSAGE.title,
              body: this.i18nService.instant(I18n.ErrorMessages.FailedToDo, {
                wantTo: this.i18nService.instant(I18n.Common.cancel),
              }),
            });
          }
        } else {
          const target = this.i18nService.instant(
            I18n.Forms.HawkerSessionForm.SessionsBrackets,
          );
          const lowerCaseDid = this.i18nService.instant(
            canceledMapping.lowerCase,
          );

          if (failedCount <= 0) {
            this.uiActions.showToasterMessage({
              type: NotificationType.SUCCESS,
              title: TOASTER_SUCCESS_MESSAGE.title,
              body: this.i18nService.instant(
                I18n.InfoMessages.PartialOrAllsuccessfully,
                { successCount, target, did: lowerCaseDid, trailSymbol: '!' },
              ),
            });
          } else {
            let errorMsg = this.i18nService.instant(
              I18n.ErrorMessages.BulkOperationFailed,
              { failedCount, target, did: lowerCaseDid },
            );

            if (successCount > 0) {
              errorMsg =
                this.i18nService.instant(
                  I18n.InfoMessages.PartialOrAllsuccessfully,
                  {
                    successCount,
                    target,
                    did: lowerCaseDid,
                    trailSymbol: '. ',
                  },
                ) + errorMsg;
            }

            this.uiActions.showToasterMessage({
              type: NotificationType.ERROR,
              title: TOASTER_ERROR_MESSAGE.title,
              body: errorMsg,
            });
          }
        }

        return of(result);
      }),
      finalize(() => {
        this.uiActions.disableLoader();
      }),
    );
  }

  public backToFrontItemMapper(doc: IHawkerSession): IHawkerSession {
    set(
      doc,
      HAWKER_SESSION_FORM_FIELD_PATH.virtualProducts,
      get(doc, 'products'),
    );

    delete doc.products;

    return flow(super.backToFrontItemMapper.bind(this))(doc);
  }

  public frontToBackItemMapper(doc: IHawkerSession): IBaseEntityType {
    const processor = this.getStatusProcessor(doc);

    return flow(processor, super.frontToBackItemMapper.bind(this))(doc);
  }

  /**
   * Returns the appropriate processor function based on document status.
   * Uses a map-based approach to determine which processor to use.
   */
  private getStatusProcessor(
    doc: IHawkerSession,
  ): (doc: IHawkerSession) => IBaseEntityType {
    const statusProcessorMap = new Map<
      string,
      (doc: IHawkerSession) => IBaseEntityType
    >([
      [
        HAWKER_SESSION_STATUS_VALUE.Canceled,
        this.processCancelStatusDocument.bind(this),
      ],
      [
        HAWKER_SESSION_STATUS_VALUE.Closed,
        this.processClosedStatusDocument.bind(this),
      ],
    ]);
    const status = get(doc, 'status', HAWKER_SESSION_STATUS_VALUE.New);

    return (
      statusProcessorMap.get(status) ??
      this.processNewOpenStatusDocument.bind(this)
    );
  }

  public processCancelStatusDocument(doc: IHawkerSession): IBaseEntityType {
    return {
      _id: doc._id,
      status: doc.status,
    };
  }

  public processClosedStatusDocument(doc: IHawkerSession): IBaseEntityType {
    return {
      ...this.processCancelStatusDocument(doc),
      products: get(doc, 'products', []),
    };
  }

  public processNewOpenStatusDocument(doc: IHawkerSession): IBaseEntityType {
    const docEmployee: IHawkerSessionEmployee = get(doc, 'employee');
    const docEvent = get(doc, 'event', {});
    const docCommissionRateEntityID: string = get(
      doc,
      'commission_rate.entity_id',
    );
    const docStatus = get(doc, 'status', HAWKER_SESSION_STATUS_VALUE.New);
    const employee = pick(docEmployee, ['id']);

    const products = get(doc, 'products', []);
    let savedProducts = chain(products)
      .filter(
        (product) =>
          !isEmpty(get(product, 'load_requested')) ||
          !isEmpty(get(product, 'returned')),
      )
      .map((product) => ({
        ...pick(product, ['entity_id']),
        load: get(product, 'load'),
        is_active: true,
        ...(!isEmpty(get(product, 'load_requested')) && {
          load_requested: parseFloat(`${get(product, 'load_requested')}`),
        }),
        ...(!isEmpty(get(product, 'returned')) && {
          returned: parseFloat(`${get(product, 'returned')}`),
        }),
      }))
      .value() as IProductWithLoad[];

    const productsChangeStatus = get(doc, 'products_change_status', []);
    if (!isEmpty(productsChangeStatus)) {
      savedProducts = [...savedProducts, ...productsChangeStatus];
      unset(doc, 'products_change_status');
    }

    const apiDoc: IHawkerSession = {
      site_id: get(doc, 'site_id'),
      status: isEqual(docStatus, HAWKER_SESSION_STATUS_VALUE.New)
        ? HAWKER_SESSION_STATUS_VALUE.Open
        : docStatus,
      employee: isEmpty(employee) ? undefined : employee,
      event: pick(docEvent, ['id']),
      commission_rate: {
        entity_id: docCommissionRateEntityID,
      },
      products: savedProducts,
    };

    return apiDoc;
  }

  public getSaleDepletionOrdersSummary(
    hawkerSessionId: string,
  ): Observable<ISaleDepletionOrdersSummaryResponse> {
    const url = `/${this.config.apiEndpoint}/${hawkerSessionId}/sale-depletion-orders`;

    return this.http.get<ISaleDepletionRawResponse>(url).pipe(
      this.catchHttpError(),
      rx.map((response): ISaleDepletionOrdersSummaryResponse => {
        return {
          products: get(response, 'products'),
          information_data: get(response, 'information_data'),
        };
      }),
    );
  }
}
